import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import Header from '../../components/ui/Header';
import CalendarGrid from './components/CalendarGrid';
import DateNavigator from './components/DateNavigator';
import FilterControls from './components/FilterControls';
import DayDetailsPanel from './components/DayDetailsPanel';
import TimelineView from './components/TimelineView';
import WeeklyStatementModal from './components/WeeklyStatementModal';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const CalendarView = () => {
  const navigate = useNavigate();
  const { currentOrgId } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month');
  const [dateBasis, setDateBasis] = useState('created_at');
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    confidence: 'all',
    fileType: 'all',
    dateRange: { from: '', to: '' },
    searchTerm: '',
    density: 'comfortable'
  });

  const { rangeStart, rangeEnd } = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 });
    return { rangeStart: start, rangeEnd: end };
  }, [currentDate]);

  useEffect(() => {
    const loadInvoices = async () => {
      if (!currentOrgId) return;
      setIsLoading(true);
      try {
        let query = supabase
          .from('parsed_invoices')
          .select('id, vendor_name, status, invoice_number, invoice_date, created_at, net_amount, vat_amount, gross_amount, currency, raw_extracted_data(data)')
          .eq('org_id', currentOrgId);

        if (dateBasis === 'created_at') {
          query = query
            .gte('created_at', startOfDay(rangeStart).toISOString())
            .lte('created_at', endOfDay(rangeEnd).toISOString());
        } else {
          query = query
            .gte('invoice_date', format(rangeStart, 'yyyy-MM-dd'))
            .lte('invoice_date', format(rangeEnd, 'yyyy-MM-dd'));
        }

        const { data, error } = await query;
        if (error) throw error;

        const transformed = (data || []).map((inv) => {
          const lines = extractLines(inv?.raw_extracted_data?.data);
          const header = {
            net: toNum(inv?.net_amount),
            vat: toNum(inv?.vat_amount),
            gross: toNum(inv?.gross_amount)
          };
          const trust = computeInvoiceTrust(lines, header, inv?.invoice_date);
          return {
            id: inv.id,
            vendorName: inv.vendor_name || 'Unknown Vendor',
            status: inv.status || 'parsed',
            invoiceNumber: inv.invoice_number || '',
            invoiceDate: inv.invoice_date || null,
            createdAt: inv.created_at || null,
            netAmount: toNum(inv?.net_amount) || 0,
            vatAmount: toNum(inv?.vat_amount) || 0,
            grossAmount: toNum(inv?.gross_amount) || 0,
            currency: inv.currency || 'GBP',
            trust
          };
        });

        transformed.sort((a, b) => {
          const da = getBasisDate(a, dateBasis);
          const db = getBasisDate(b, dateBasis);
          if (!da || !db) return 0;
          return db.getTime() - da.getTime();
        });

        setInvoices(transformed);
      } catch (error) {
        console.error('Error fetching invoices:', error);
        setInvoices([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadInvoices();
  }, [currentOrgId, dateBasis, rangeEnd, rangeStart]);

  const invoicesByDate = useMemo(() => {
    const grouped = {};
    for (const inv of invoices) {
      const date = getBasisDate(inv, dateBasis);
      if (!date || !isValid(date)) continue;
      const key = format(date, 'yyyy-MM-dd');
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(inv);
    }
    return grouped;
  }, [invoices, dateBasis]);

  useEffect(() => {
    if (!selectedDate) return;
    const key = format(selectedDate, 'yyyy-MM-dd');
    setSelectedInvoices(invoicesByDate[key] || []);
  }, [selectedDate, invoicesByDate]);

  const handleDateSelect = (date, dayInvoices) => {
    setSelectedDate(date);
    setSelectedInvoices(dayInvoices || []);
    setIsPanelOpen(true);
  };

  const handleViewWeekReport = (startDate, endDate) => {
    setSelectedWeek({ startDate, endDate });
  };

  const handleOpenInvoice = (invoiceId) => {
    if (!invoiceId) return;
    navigate(`/invoice-editor?id=${invoiceId}`);
  };

  const handleExport = () => {
    console.log('Exporting calendar data with filters:', filters);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Calendar View</h1>
              <p className="text-sm text-muted-foreground">
                Upload productivity and invoice-date financial tracking
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleExport}
                iconName="Download"
                iconPosition="left"
              >
                Export Data
              </Button>
              <Button
                variant="default"
                onClick={() => window.location.href = '/uploads'}
                iconName="Upload"
                iconPosition="left"
              >
                New Upload
              </Button>
            </div>
          </div>

          <FilterControls
            filters={filters}
            onFiltersChange={setFilters}
            onExport={handleExport}
            dateBasis={dateBasis}
            onDateBasisChange={setDateBasis}
          />

          <DateNavigator
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />

          <div className="space-y-4">
            {isLoading && (
              <div className="text-sm text-muted-foreground">Loading invoices…</div>
            )}
            {viewMode === 'month' ? (
              <CalendarGrid
                currentDate={currentDate}
                invoicesByDate={invoicesByDate}
                onDateSelect={handleDateSelect}
                selectedDate={selectedDate}
                dateBasis={dateBasis}
                onViewWeekReport={handleViewWeekReport}
              />
            ) : (
              <TimelineView
                invoices={invoices}
                dateBasis={dateBasis}
                onOpenInvoice={handleOpenInvoice}
              />
            )}
          </div>
        </div>
      </main>

      <DayDetailsPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        date={selectedDate}
        invoices={selectedInvoices}
        dateBasis={dateBasis}
        onOpenInvoice={handleOpenInvoice}
      />

      <WeeklyStatementModal
        isOpen={!!selectedWeek}
        onClose={() => setSelectedWeek(null)}
        startDate={selectedWeek?.startDate}
        endDate={selectedWeek?.endDate}
        dateBasis={dateBasis}
      />
    </div>
  );
};

const toNum = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const extractLines = (raw) => {
  const lines = raw?.current?.line_items || raw?.parsed_json?.line_items || raw?.line_items || [];
  return lines.map((line) => ({
    unitPrice: toNum(line?.unit_price),
    netAmount: toNum(line?.net_amount),
    vatAmount: toNum(line?.vat_amount),
    total: toNum(line?.total)
  }));
};

const computeInvoiceTrust = (lines, header, invoiceDate) => {
  const sums = (lines || []).reduce(
    (acc, line) => ({
      net: acc.net + (toNum(line?.netAmount) || 0),
      vat: acc.vat + (toNum(line?.vatAmount) || 0),
      gross: acc.gross + (toNum(line?.total) || 0),
      zeros:
        acc.zeros +
        ((toNum(line?.unitPrice) === 0 && toNum(line?.netAmount) === 0 && toNum(line?.total) === 0)
          ? 1
          : 0)
    }),
    { net: 0, vat: 0, gross: 0, zeros: 0 }
  );

  const deltas = {
    net: Math.abs((toNum(header?.net) || 0) - sums.net),
    vat: Math.abs((toNum(header?.vat) || 0) - sums.vat),
    gross: Math.abs((toNum(header?.gross) || 0) - sums.gross)
  };

  const reconciled = deltas.net < 0.01 && deltas.vat < 0.01 && deltas.gross < 0.01;
  const allZeroLines = (lines?.length || 0) > 0 && sums.zeros === lines.length;

  const trusted = reconciled && !allZeroLines;
  let reason = null;
  if (!trusted) {
    if (!reconciled) reason = 'lines_totals_mismatch';
    else if (allZeroLines) reason = 'zero_lines';
  }

  return { trusted, reconciled, deltas, allZeroLines, reason };
};

const getBasisDate = (invoice, dateBasis) => {
  const raw = dateBasis === 'created_at' ? invoice?.createdAt : invoice?.invoiceDate;
  if (!raw) return null;
  const parsed = parseISO(raw);
  return isValid(parsed) ? parsed : null;
};

export default CalendarView;
